import { useMemo, type RefObject } from "react";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { SheetLabel, TableGlyph, sheetRowStyle } from "./InsertSheetParts";
import { TablePreview, previewLayout, readTable, useBoxWidth } from "./TablePreview";
import { tileText, tileRemainder } from "./tableTiles";
import type { TableMatch } from "./tableSearch";
import { splitRecent } from "./recentTables";
import { editedAgo, inPapers, tableName, tableSize, unseenPapers } from "./tableWords";
import type { TableUsage } from "./usePaper";
import type { TableSummary } from "./useTables";
import { testIds } from "@/testIds";

/**
 * 1a, pick a table: the left side is where you choose, the right side is what
 * you would be choosing, typeset as it will print.
 */

export const rowDomId = (id: string) => `table-insert-option-${id}`;

/** Where the query was found, in a word or two. */
function matchNote(m: TableMatch | null | undefined, t: TableSummary): string | null {
  if (!m || m.kind === "name") return null;
  if (m.kind === "label") return `label · ${t.label}`;
  const data = readTable(t.content);
  if (!data) return null;
  const text = tileText(data);
  if (m.kind === "column") return `column · “${text.header?.[m.c] ?? ""}”`;
  return `cell · “${text.rows[m.r]?.[m.c] ?? ""}”`;
}

/** The order the sheet lists tables in: Recent, then All; matches alone while searching. */
export function tableOrder(rows: TableSummary[], query: string): { recent: TableSummary[]; rest: TableSummary[] } {
  return query.trim() ? { recent: [], rest: rows } : splitRecent(rows);
}

export function TableList({
  recent, rest, total, loading, matches, chosen, onChoose, onCommit, query, onQuery, searchRef, listRef, onNewTable,
}: {
  recent: TableSummary[];
  rest: TableSummary[];
  /** How many tables the author has, searched or not. */
  total: number;
  loading?: boolean;
  matches: Map<string, TableMatch | null>;
  chosen: string | null;
  onChoose: (id: string) => void;
  onCommit: () => void;
  query: string;
  onQuery: (q: string) => void;
  searchRef: RefObject<HTMLInputElement | null>;
  listRef: RefObject<HTMLDivElement | null>;
  /** New table instead — named after the query when there is one. */
  onNewTable: (name: string) => void;
}) {
  const q = query.trim();
  const count = recent.length + rest.length;

  const row = (t: TableSummary) => {
    const on = t.id === chosen;
    const note = matchNote(matches.get(t.id), t);
    return (
      <div
        key={t.id}
        id={rowDomId(t.id)}
        role="option"
        aria-selected={on}
        data-testid={testIds.tableTool.insertRow}
        data-row-id={t.id}
        data-table-id={t.id}
        data-selected={on || undefined}
        onClick={() => onChoose(t.id)}
        onDoubleClick={onCommit}
        className="flex cursor-pointer items-center gap-[11px] rounded-[9px] px-2.5 py-[9px] hover:bg-[var(--lilia-hover)]"
        style={sheetRowStyle(on)}
      >
        <TableGlyph on={on} />
        <div className="min-w-0 flex-1">
          <div className={`truncate text-[13px] text-[color:var(--lilia-text)] ${on ? "font-semibold" : "font-medium"}`}>
            {tableName(t)}
          </div>
          <div
            className="mt-px truncate text-[11.5px]"
            style={{ color: on ? "var(--lilia-text-secondary)" : "var(--lilia-text-secondary)", fontVariantNumeric: "tabular-nums" }}
          >
            {note ?? `${tableSize(t)} · ${inPapers(t.documentCount)}`}
          </div>
        </div>
        {on && <span className="text-[11px] font-semibold text-[color:var(--lilia-primary)]">↵</span>}
      </div>
    );
  };

  return (
    <div className="flex w-[400px] shrink-0 flex-col border-r border-[var(--lilia-border)]">
      <div className="px-3.5 pb-2.5 pt-3.5">
        <label
          className="flex h-10 items-center gap-[9px] rounded-[9px] border px-3 focus-within:border-[var(--lilia-primary)] focus-within:shadow-[0_0_0_3px_var(--lilia-primary-subtle)]"
          style={{ background: "var(--lilia-bg)", borderColor: "var(--lilia-border-strong)" }}
        >
          <IconSearch size={15} className="shrink-0 text-[color:var(--lilia-text-muted)]" />
          <input
            ref={searchRef}
            autoFocus
            data-testid={testIds.tableTool.insertSearch}
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search your tables"
            aria-label="Search your tables"
            aria-controls="table-insert-list"
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] text-[color:var(--lilia-text)] shadow-none outline-none focus-visible:outline-none placeholder:text-[color:var(--lilia-text-secondary)]"
          />
          <span className="shrink-0 text-[11px] text-[color:var(--lilia-text-secondary)]">names, columns, cells</span>
        </label>
      </div>

      <div
        ref={listRef}
        id="table-insert-list"
        role="listbox"
        tabIndex={0}
        aria-label="Your tables"
        aria-activedescendant={chosen ? rowDomId(chosen) : undefined}
        className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-2 outline-none focus-visible:outline-none"
      >
        {loading ? (
          <div className="flex flex-col gap-2 p-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-[46px] rounded-[9px]" style={{ background: "var(--lilia-bg-secondary)" }} />)}
          </div>
        ) : count === 0 ? (
          <div data-testid={testIds.tableTool.insertEmpty} className="flex flex-col gap-2.5 px-3 py-6">
            {q ? (
              <>
                <div className="text-[13.5px] font-semibold text-[color:var(--lilia-text)]">No table matches “{q}”</div>
                <div className="text-[12.5px] leading-[1.6] text-[color:var(--lilia-text-secondary)]">
                  Searched names, columns and cells in your {total} table{total === 1 ? "" : "s"}.
                </div>
              </>
            ) : (
              <div className="text-[13.5px] font-semibold text-[color:var(--lilia-text)]">No tables yet</div>
            )}
            <div>
              <button
                type="button"
                data-testid={testIds.tableTool.insertNewTable}
                onClick={() => onNewTable(q)}
                className="inline-flex h-8 items-center rounded-[8px] border border-[var(--lilia-border-strong)] px-3 text-[12.5px] font-medium text-[color:var(--lilia-text)] hover:bg-[var(--lilia-hover)]"
                style={{ background: "var(--lilia-surface)" }}
              >
                {q ? `New table “${q}”` : "New table"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {recent.length > 0 && <SheetLabel className="px-2 pb-1 pt-2">Recent</SheetLabel>}
            {recent.map(row)}
            <SheetLabel className={`px-2 pb-1 ${recent.length ? "pt-3" : "pt-2"}`}>
              {q ? `Matches · ${count}` : `All tables · ${total}`}
            </SheetLabel>
            {rest.map(row)}
          </>
        )}
      </div>

      {count > 0 && (
        <button
          type="button"
          data-testid={testIds.tableTool.insertNewTable}
          onClick={() => onNewTable("")}
          className="flex items-center gap-2 border-t border-[var(--lilia-border)] px-4 py-2.5 text-left text-[12.5px] font-semibold text-[color:var(--lilia-primary-ink)] hover:bg-[var(--lilia-hover)]"
        >
          <IconPlus size={14} /> New table instead
        </button>
      )}
    </div>
  );
}

/** The chosen table, typeset, with where it is already used. */
export function TableDetail({
  table, match, usage,
}: {
  table: TableSummary | undefined;
  match: TableMatch | null | undefined;
  usage: TableUsage | null | undefined;
}) {
  const box = useBoxWidth(720);
  const data = useMemo(() => (table ? readTable(table.content) : null), [table]);
  const text = useMemo(() => (data ? tileText(data) : null), [data]);
  const height = 330;
  const layout = text ? previewLayout(text, "sheet", box.width, height) : null;
  const remainder = layout ? tileRemainder(layout) : "";
  const hidden = usage ? usage.total - usage.visible.length : 0;

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden" style={{ background: "var(--lilia-bg-secondary)" }}>
      {table ? (
        <>
          <div className="flex items-baseline gap-2.5 px-[22px] pb-3 pt-[18px]">
            <span className="min-w-0 truncate text-[16px] font-semibold text-[color:var(--lilia-text)]">{tableName(table)}</span>
            {table.label.trim() && (
              <span className="shrink-0 text-[11px] text-[color:var(--lilia-text-secondary)]" style={{ fontFamily: "var(--lilia-font-mono)" }}>
                {table.label}
              </span>
            )}
            <div className="flex-1" />
            <span className="shrink-0 text-[11.5px] text-[color:var(--lilia-text-secondary)]">
              {table.updatedAt ? `edited ${editedAgo(table.updatedAt)}${table.isOwner ? " by you" : ""}` : ""}
            </span>
          </div>

          <div ref={box.ref} className="mx-[22px]" style={{ boxShadow: "0 1px 2px var(--lilia-shadow-lg)", borderRadius: 10 }}>
            {/* Keyed: a swap on ↑↓ is a short crossfade, never a slide (1f). */}
            {data ? (
              <div key={table.id} className="lilia-preview-swap">
                <TablePreview
                  data={data}
                  variant="sheet"
                  width={box.width}
                  height={height}
                  highlight={match && (match.kind === "cell" || match.kind === "column")
                    ? { r: match.kind === "cell" ? match.r : -1, c: match.c }
                    : null}
                />
              </div>
            ) : (
              <div className="grid h-[120px] place-items-center rounded-[10px] text-[12px] text-[color:var(--lilia-text-secondary)]" style={{ background: "var(--lilia-bg)" }}>
                Preview unavailable
              </div>
            )}
          </div>

          {layout && (
            <div className="mx-[22px] mt-2 flex flex-wrap gap-3.5 text-[11.5px] text-[color:var(--lilia-text-secondary)]" style={{ fontVariantNumeric: "tabular-nums" }}>
              <span>{layout.totalRows} row{layout.totalRows === 1 ? "" : "s"} · {layout.totalCols} column{layout.totalCols === 1 ? "" : "s"}</span>
              {remainder && <span>{remainder} not shown</span>}
              <span>Preview is typeset, as it will print</span>
            </div>
          )}

          <div data-testid={testIds.tableTool.insertUsedIn} className="mx-[22px] mt-[18px] flex flex-col gap-[7px]">
            <SheetLabel>Already used in</SheetLabel>
            <div className="flex flex-wrap gap-1.5">
              {usage === undefined ? (
                <span className="h-[26px] w-40 rounded-[13px]" style={{ background: "var(--lilia-bg)" }} />
              ) : !usage || usage.total === 0 ? (
                <span className="text-[12px] text-[color:var(--lilia-text-secondary)]">Not in a paper yet</span>
              ) : (
                <>
                  {usage.visible.map((u) => (
                    <span key={u.documentId} className="inline-flex h-[26px] items-center rounded-[13px] border border-[var(--lilia-border)] px-2.5 text-[12px] text-[color:var(--lilia-text)]" style={{ background: "var(--lilia-bg)" }}>
                      {u.documentTitle || "Untitled"}
                    </span>
                  ))}
                  {hidden > 0 && (
                    <span className="inline-flex h-[26px] items-center rounded-[13px] border border-[var(--lilia-border)] px-2.5 text-[12px] text-[color:var(--lilia-text-secondary)]" style={{ background: "var(--lilia-bg)" }}>
                      {unseenPapers(hidden)}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="grid flex-1 place-items-center text-[12.5px] text-[color:var(--lilia-text-secondary)]">
          Choose a table to see it here.
        </div>
      )}
    </div>
  );
}
